import { ViewController } from '@estee/elc-service';
import { action, observable, computed, set } from 'mobx';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';
import { UserRepository } from '../../repositories/UserRepository';
import { IUser } from '../../domain/entities/User';
import { SignUpType } from '../../../constants/SignUpTypeEnum';
// TODO: APACFTRS1-770 city and district drop down options will be obtained from static JSON file, code kept for quick reference, remove after refactor is done
import { buildStoreDropdown } from '../../utils/DropDownUtils';
import { ISignUpProps } from '../../views/signup/SignUp';
import { SignUpFormLoadedUseCase } from '../../../use-cases/SignUpFormLoadedUseCase';
import { SignUpSubmittedUseCase } from '../../../use-cases/SignUpSubmittedUseCase';
import {
    ICheckableItem,
    ICheckboxGroup,
    IDropdownOption,
    InputSchemaType,
    isCheckboxGroup,
    IValidatorMessages
} from '../../domain/entities/Config';
import { TranslationRepository } from '../../repositories/TranslationRepository';
import { ConfigRepository } from '../../repositories/ConfigRepository';
import { FieldValidations } from '../../utils/FieldValidations';
import { SignUpOrigin } from '../../../constants/SignUpOriginEnum';

export class SignUpViewController extends ViewController {
    private userRepository: UserRepository;
    private translationRepository: TranslationRepository;
    private configRepository: ConfigRepository;
    @observable public values: IUser;
    @observable private allContactPrefsChecked: boolean = false;
    @observable public isOverlayOpen: boolean = false;
    @observable private isStoreIdProvided: boolean = false;
    @observable private countryCodeDropdownOptions: IDropdownOption[] = [];
    @observable private locationDropdownOptions: IDropdownOption[] = [];
    @observable private storeDropdownOptions: IDropdownOption[] = [];
    @observable private preferredLanguageDropdownOptions: IDropdownOption[] = [];
    @observable public isFormLoading: boolean = false;
    @observable public isScrolled: boolean = true;
    @observable public firstErrorDomElement: HTMLElement;

    constructor() {
        super();
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.translationRepository = diContainer.get(serviceNames.translationRepository);
        this.configRepository = diContainer.get(serviceNames.configRepository);
    }

    @action
    public setValues = (values: IUser) => {
        set(this.values, values);
    };

    @computed
    private get formStore() {
        return this.userRepository.userFormStore;
    }

    @action
    public onCloseOverlay = () => {
        // TODO: APACFTRS1-974 clear PII data
        // this.userRepository.userFormStore.clearStore();
        this.userRepository.resetOverlay();
        this.userRepository.clearErrorMessages();
    };

    @action
    public onOverlayAccordionChange = () => {
        this.userRepository.clearErrorMessages();
    };

    @action
    public setFormLoading = (isLoading: boolean) => {
        this.isFormLoading = isLoading;
    };

    private setFieldValidatorMessages(
        validatorMessages: Record<string, IValidatorMessages>,
        item: InputSchemaType
    ) {
        if (isCheckboxGroup(item)) {
            (item as ICheckboxGroup).items.forEach((checkbox) => {
                this.setFieldValidatorMessages(validatorMessages, checkbox as ICheckableItem);
            });
        } else {
            const { name, errorMessages } = item;
            if (errorMessages) {
                validatorMessages[name] = errorMessages;
            }
        }
    }

    // All sign up form's field validations definitions, with translations applied.
    @computed
    public get fieldsValidations(): Record<string, FieldValidations> {
        if (!this.configRepository.rampConfig?.signUpFormInputSchema) {
            return {};
        }
        const {
            signUpFormInputSchema: { sections },
            fieldsValidations: configs
        } = this.configRepository.rampConfig;
        const { translations } = this.translationRepository;

        const fieldsValidatorMessages = sections.reduce((validatorMessages, section) => {
            const messages: Record<string, IValidatorMessages> = {};
            section.items.forEach((item) => this.setFieldValidatorMessages(messages, item));

            return { ...validatorMessages, ...messages };
        }, {}) as Record<string, IValidatorMessages>;

        return Object.keys(configs).reduce((fieldsValidations, fieldName) => {
            const fieldValidations = new FieldValidations({
                ...configs[fieldName],
                translations,
                errorMessages: fieldsValidatorMessages[fieldName]
            });

            return {
                ...fieldsValidations,
                [fieldName]: fieldValidations
            };
        }, {});
    }

    @computed
    public get data() {
        const {
            isExistingUser,
            overlay: { userType },
            onlineSignUpFormStore,
            errorMessages
        } = this.userRepository;

        return {
            allContactPrefsChecked: this.allContactPrefsChecked,
            formStore: this.formStore,
            onlineSignUpFormStore,
            isExistingUser,
            userType,
            isStoreIdProvided: this.isStoreIdProvided,
            countryCodeDropdownOptions: this.countryCodeDropdownOptions,
            locationDropdownOptions: this.locationDropdownOptions,
            storeDropdownOptions: this.storeDropdownOptions,
            // birthDayDropdownOptions: this.birthDayDropdownOptions,
            // birthMonthDropdownOptions: this.birthMonthDropdownOptions,
            errorMessages,
            preferredLanguageDropdownOptions: this.preferredLanguageDropdownOptions,
            translations: this.translationRepository.translations,
            signUpSchema: this.configRepository.rampConfig?.signUpFormInputSchema,
            fieldsValidations: this.fieldsValidations,
            validationObject: this.formStore.errors
        };
    }

    @action
    public signUpButtonClicked = async (
        type: SignUpType,
        origin: SignUpOrigin = SignUpOrigin.SIGNUP_FORM
    ) => {
        this.setFormLoading(true);
        const signUpSubmittedUseCase = new SignUpSubmittedUseCase(type, origin);
        await signUpSubmittedUseCase.execute();
        this.setFormLoading(false);
    };

    @action
    public loaded = async (props: ISignUpProps) => {
        await this.initBasicInfoDropdowns(props);

        const signUpFormLoadedUseCase = new SignUpFormLoadedUseCase();
        signUpFormLoadedUseCase.execute();
    };

    @action
    public initBasicInfoDropdowns = async (props: ISignUpProps) => {
        // // this.signUpBasicInfoProps = props;
        // const { storeList } = this.translationRepository.translations;
        // // const { contactLanguageOptions } = props.config.rampForm as IRampBUConfig;
        // const countries = await this.userRepository.getCountries();
        // if (storeList) {
        //     this.storeDropdownOptions = this.prependPleaseSelectOption(
        //         props,
        //         buildStoreDropdown(storeList)
        //     );
        // }
        // // this.birthDayDropdownOptions = this.prependPleaseSelectOption(
        // //     props,
        // //     buildNumericDropdownOptions(1, 31)
        // // );
        // // this.birthMonthDropdownOptions = this.prependPleaseSelectOption(
        // //     props,
        // //     buildNumericDropdownOptions(1, 12)
        // // );
        // // if (contactLanguageOptions) {
        // //     this.preferredLanguageDropdownOptions = this.prependPleaseSelectOption(
        // //         props,
        // //         contactLanguageOptions
        // //     );
        // // }
        // if (countries && countries.length) {
        //     this.countryCodeDropdownOptions = this.prependPleaseSelectOption(
        //         props,
        //         buildCountryCodeDropdown(countries)
        //     );
        //     this.locationDropdownOptions = this.prependPleaseSelectOption(
        //         props,
        //         buildLocationDropdown(countries)
        //     );
        //     this.formStore.setFieldValue('countryCode', this.countryCodeDropdownOptions[0].value);
        //     this.formStore.setFieldValue('location', this.locationDropdownOptions[0].value);
        // }
    };

    // TODO: check and remove this, should be handled in CheckboxGroup component (APACFTRS1-770)
    @action
    public onCBChange = (isChecked: boolean, value: string) => {
        const contactGroup = [
            'smsPromotions',
            'phonePromotions',
            'emailPromotions',
            'postalPromotions'
        ];
        const checkedValue = isChecked ? 'on' : undefined;
        if (value === 'allContactPrefsChecked') {
            this.allContactPrefsChecked = isChecked;
            contactGroup.forEach((val) => {
                this.formStore.setFieldValue(val, checkedValue);
            });
        } else {
            this.formStore.setFieldValue(value, checkedValue);
            const isAlreadyChecked = (item: string) => this.formStore.values[item];
            this.allContactPrefsChecked = contactGroup.every(isAlreadyChecked);
        }
    };

    @action
    public prependPleaseSelectOption = (props: ISignUpProps, options: IDropdownOption[]) => {
        const { selectLabel } = this.translationRepository.translations;
        if (selectLabel) {
            options.unshift({
                label: selectLabel,
                value: ''
            });
        }

        return options;
    };

    @action
    public isDefaultExists = (storeId: string) => {
        const { storeList } = this.translationRepository.translations;

        return buildStoreDropdown(storeList).some(
            (store) => store.value.toUpperCase() === storeId.toUpperCase()
        );
    };

    public validateForm = () => {
        Object.keys(this.formStore.errors).forEach((fieldName) => {
            this.formStore.setTouchedField(fieldName);
        });
    };

    @action
    public signUpHandler = () => {
        this.isScrolled = false;
        this.formStore.isValid ? this.signUpButtonClicked(SignUpType.RAMP) : this.validateForm();
    };

    @computed
    public get shouldScroll(): boolean {
        return !this.isScrolled && (!this.formStore.isValid || !!this.data.errorMessages);
    }

    @action
    public scrollComplete = () => {
        this.isScrolled = true;
    };
}
